import { useMemo } from 'react';

import { useCurrentUser } from '@bonusly/who-am-i';

import { ReferencedRecognition, User } from '../../../lib';
import { ReferencedFeedbackCategory } from '../../../lib/entities/referenced-feedback-category';

export type OpenFeedbackRequestEventData = {
	defaultText?: string;
	defaultUsers?: {
		id: string;
		imageUrl: string;
		name: string;
		username: string;
	}[];
	reference?: any;
};

type Props = {
	defaultText?: string;
	defaultUsers: User[];
	key: string;
	reference?: ReferencedRecognition;
};

export const useFeedbackModalProps = (data: OpenFeedbackRequestEventData) => {
	const user = useCurrentUser();

	return useMemo(() => {
		const props: Props = {
			defaultText: undefined,
			defaultUsers: [],
			key: Math.random().toString(),
			reference: undefined,
		};

		if (data.defaultUsers) {
			props.defaultUsers = data.defaultUsers;
		}

		if (data.defaultText) {
			props.defaultText = data.defaultText;
		}

		// NOTE: The `reference` sent in this event can be either a Bonus or a Category of type Feedback related to one-on-ones.
		// There is no specific distinction in the event structure at this time, so we need to check for unique fields to determine the type.
		// In this case, the Bonus has a recipient_id, which is used to determine the type. These events should be refactored to be more
		// specific.
		if (data.reference?.reason) {
			const bonus = data.reference;
			const users = bonus.receivers
				.map((user: any) => {
					return new User({
						id: user.id,
						imageUrl: user.profile_pic_url,
						name: user.display_name,
						username: user.username,
					});
				})
				.filter((receiver: any) => receiver.id !== user.id);

			const giver = new User({
				id: bonus.giver.id,
				imageUrl: bonus.giver.profile_pic_url,
				name: bonus.giver.display_name,
				username: bonus.giver.username,
			});

			const reference = new ReferencedRecognition({
				id: bonus.id,
				text: bonus.reason,
				date: bonus.created_at,
				giver,
			});

			const defaultText = bonus.traits?.find((trait: any) => trait.name === 'feedback_candidate')?.data.prompt;

			Object.assign(props, {
				defaultText,
				// NOTE: if default users are passed in, use those instead of giver and receivers
				defaultUsers: data.defaultUsers || [giver, ...users],
				key: reference.id,
				reference,
			});
		}

		// If the reference is a Feedback Category
		// FIXME: We need to refactor entities into the api package to avoid circular dependencies and allow us to import them here
		// Referencing these by the presense of a property is prone to errors and is a temporary solution.
		if (data.reference?.agendaTopics) {
			Object.assign(props, {
				reference: new ReferencedFeedbackCategory({
					id: data.reference.id,
				}),
			});
		}

		return props;
	}, [data, user]);
};
